<template>
	<BaseModal
		content-classes="vcx-all-holdings-modal__container vcx-p-150 vcx-p-300-md"
		container-classes="vcx-all-holdings-modal"
		data-context="Vcx all holdings"
		@modal-close="close"
	>
		<header>
			<div class="vcx-all-holdings-modal__title-row">
				<h2 class="vcx-h5 vcx-h4-md vcx-text-inverse">All VCX holdings</h2>
				<BaseButton :no-style="true" class="vcx-all-holdings-modal__close" @click.stop="close">
					<span class="vcx-flex vcx-items-center vcx-text-inverse"
						><BaseSvgIcon name="close" width="16" height="16" class="vcx-mr-25" /><span
							class="vcx-body-lg-bold"
							>Close</span
						></span
					>
				</BaseButton>
			</div>
			<span class="vcx-body-md vcx-text-gray-40">{{ asOfLabel }}</span>
		</header>
		<div class="vcx-all-holdings-modal__content">
			<table class="vcx-all-holdings-modal__table">
				<thead class="vcx-mt-25">
					<tr class="vcx-body-lg-bold vcx-text-gray-40">
						<th>Company</th>
						<th>Allocation</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="holding in holdings" :key="holding.key" class="vcx-text-inverse vcx-body-lg-bold">
						<td>{{ holding.key }}</td>
						<td class="vcx-body-lg-bold">
							{{ holding.value }}
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import BaseModal from '@components/_base-modal.vue';
import { SanityKeyValueData } from 'types/sanity/charts-data';

interface Props {
	asOfLabel: string;
	holdings: Array<SanityKeyValueData>;
}

defineProps<Props>();

interface Emits {
	(e: 'modal-close'): void;
}

const emits = defineEmits<Emits>();

function close(): void {
	emits('modal-close');
}
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive' as *;

:deep(.vcx-all-holdings-modal) {
	width: 100%;
	margin: 0 auto;

	@include vcx-media-md {
		max-width: 66.666%;
	}
}

:deep(.vcx-all-holdings-modal__container) {
	background-color: var(--vcx-bg-dark);
	overflow: hidden;
	max-height: 90vh;
}

:deep(.vcx-all-holdings-modal__title-row) {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--vcx-space-50);
	margin-bottom: var(--vcx-space-25);

	@include vcx-media-md {
		gap: var(--vcx-space-200);
	}
}

:deep(.vcx-all-holdings-modal__close) {
	&:hover {
		opacity: 0.8;
	}
}

:deep(.vcx-all-holdings-modal__content) {
	flex: 1;
	overflow-y: auto;
	max-height: calc(90vh - 120px);

	@include vcx-media-md {
		max-height: 60vh;
	}
}

:deep(.vcx-all-holdings-modal__table) {
	width: 100%;

	thead {
		position: sticky;
		top: 0;
		background-color: var(--vcx-bg-dark);

		tr {
			padding: var(--vcx-space-100) 0;
			border-bottom: 1px solid var(--vcx-gray-50);
			margin-bottom: var(--vcx-space-100);
		}
	}

	tbody tr {
		border-bottom: 1px solid var(--vcx-border-dark);

		&:last-child {
			border-bottom: none;
		}
	}

	tr {
		display: flex;
		justify-content: space-between;
		gap: var(--vcx-space-100);
		align-items: center;
		padding: var(--vcx-space-100) 0;
	}
}

:deep(.vcx-all-holdings-modal__value) {
	font-family: var(--vcx-font-heading);
}
</style>
